/**
 * Copyright (C) 2023 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */
import React, { useCallback, useContext, useEffect, useState } from 'react';
import bbox from '@turf/bbox';
import { lineString } from '@turf/helpers';
import isNil from 'lodash/isNil';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { ThemeProvider } from 'styled-components';
import { StyledEngineProvider } from '@mui/material/styles';
import { Waypoint } from '../../../shared/entities/flightLegServiceAlerts';
import {
  selectFlightLegsInfoById,
  selectFlightLegServiceAlerts,
  selectMissionPlanById,
  selectSelectedIdOfMissionPlanById,
  selectUserId,
  selectSelectedOverlaidFlightLegId,
  selectIsRequestingFlightLegServiceAlerts,
  selectLoadingFlightLegsInfoSuccess,
} from '../../../store';
import { requestBeams } from '../../../store/beams/actions';
import { setHoveredBeam } from '../../../store/ui/actions';
import { MapContext } from '../../common/map/BaseMapComponent';
import appTheme from '../../common/theme/appTheme.json';
import { FTV_MAP_ZOOM_BUFFER } from '../../fleet/FullTailView/FullTailViewMapZoomContainer';
import MptFlightPathContainer from '../../fleet/MptFlightPath/MptFlightPathContainer';
import MptServiceAlertsContainer from '../../fleet/MptFlightPath/MptServiceAlertsContainer';
import { CHART_SIDE_PADDING, CHART_WIDTH } from './TimelineChart/PerformanceOutlookHorizontalTimeline';
import { MapActionsContext } from '../../common/map/BaseMapContainer';
import { PERFORMANCE_OUTLOOK_MAP_ID } from '../../../store/maps/actions';
import MptFlightPathWaypointsContainer from '../../fleet/MptFlightPath/MptFlightPathWaypointsContainer';
import { clearServiceAlerts, getServiceAlerts } from '../../../store/flightLegServiceAlerts/actions';
import { getArcLightMapVersion, getViHnaMapVersion } from "../../../utils/BeamUtils";

export const PERFORMANCE_OUTLOOK_MAP_DEFAULT_WIDTH = '100%';
export const PERFORMANCE_OUTLOOK_MAP_DEFAULT_HEIGHT = '425px';

export const PERFORMANCE_OUTLOOK_MAP_TOP_PX = 104;
export const PERFORMANCE_OUTLOOK_MAP_LEFT_PX = 0;
export interface PerformanceOutlookMapProps {
  map?: google.maps.Map;
}

function PerformanceOutlookMap({ map }: PerformanceOutlookMapProps) {
  const dispatch = useDispatch();
  const userId = useSelector(selectUserId, shallowEqual);
  const currentMission = useSelector(selectMissionPlanById, shallowEqual);
  const missionPlanId = useSelector(selectSelectedIdOfMissionPlanById, shallowEqual);
  const flightLegs = useSelector(selectFlightLegsInfoById, shallowEqual);
  const loadingFlightLegsSuccess = useSelector(selectLoadingFlightLegsInfoSuccess, shallowEqual);
  const flightLegServiceAlerts = useSelector(selectFlightLegServiceAlerts, shallowEqual);
  const selectedOverlaidFlightLegIdPO = useSelector(
    selectSelectedOverlaidFlightLegId(PERFORMANCE_OUTLOOK_MAP_ID),
    shallowEqual,
  );

  const isRequestingFlightLegServiceAlerts = useSelector(selectIsRequestingFlightLegServiceAlerts, shallowEqual);
  const [initialGeojson, setInitialGeojson] = useState<boolean>(false);
  const overlaidFlightLegHidden = false;

  const mapActionsContext = useContext(MapActionsContext);
  const {
    setMapDimensions,
    clearMapDimensions,
    setMapPosition,
    clearMapPosition,
    addBeamToMap,
    clearBeamsOnMap,
    setFlightPathBeamsChecked,
    setFlightPathBeamsDisabled,
    setFlightPathWaypointsDisabled,
    setFlightPathWaypointsChecked,
    setMapHidden,
  } = mapActionsContext.mapActions;

  const setHoveredBeamHandler = useCallback(
    (beamName: string) => {
      dispatch(setHoveredBeam(beamName));
    },
    [dispatch],
  );

  const addBeamToMapHandler = useCallback(
    (name: string, networkType: string, mapType: string) => {
      dispatch(addBeamToMap(name, networkType, mapType));
    },
    [dispatch, addBeamToMap],
  );

  const resizeMap = useCallback(() => {
    const actualWidth =
      window.innerWidth - 52 <= CHART_WIDTH + 2 * CHART_SIDE_PADDING
        ? `${CHART_WIDTH + 2 * CHART_SIDE_PADDING}px`
        : PERFORMANCE_OUTLOOK_MAP_DEFAULT_WIDTH;
    dispatch(setMapDimensions(actualWidth, PERFORMANCE_OUTLOOK_MAP_DEFAULT_HEIGHT));
  }, [dispatch, setMapDimensions]);

  // On mount (and unmount)
  useEffect(() => {
    window.addEventListener('resize', resizeMap);

    dispatch(clearBeamsOnMap());

    const actualWidth =
      window.innerWidth - 52 <= CHART_WIDTH + 2 * CHART_SIDE_PADDING
        ? `${CHART_WIDTH + 2 * CHART_SIDE_PADDING}px`
        : PERFORMANCE_OUTLOOK_MAP_DEFAULT_WIDTH;
    dispatch(setMapDimensions(actualWidth, PERFORMANCE_OUTLOOK_MAP_DEFAULT_HEIGHT));

    dispatch(setFlightPathBeamsDisabled(false));
    dispatch(setFlightPathBeamsChecked(true));
    dispatch(setFlightPathWaypointsDisabled(false));
    dispatch(setFlightPathWaypointsChecked(false));
    return () => {
      window.removeEventListener('resize', resizeMap);

      dispatch(clearBeamsOnMap());
      dispatch(clearServiceAlerts());
      dispatch(clearMapDimensions());
      dispatch(clearMapPosition());
    };
  }, [
    dispatch,
    resizeMap,
    setMapDimensions,
    setFlightPathBeamsDisabled,
    setFlightPathBeamsChecked,
    setFlightPathWaypointsDisabled,
    setFlightPathWaypointsChecked,
    clearBeamsOnMap,
    clearMapDimensions,
    clearMapPosition,
  ]);

  useEffect(() => {
    dispatch(setMapPosition(PERFORMANCE_OUTLOOK_MAP_TOP_PX, PERFORMANCE_OUTLOOK_MAP_LEFT_PX));
  }, [setMapPosition, dispatch]);

  useEffect(() => {
    dispatch(setMapHidden(false));

    return () => {
      dispatch(setMapHidden(true));
    };
  }, [dispatch, setMapHidden]);

  // /servicealerts and /geojson api requests
  useEffect(() => {
    if (!isNil(missionPlanId) && !isNil(userId) && selectedOverlaidFlightLegIdPO && loadingFlightLegsSuccess) {
      dispatch(getServiceAlerts(missionPlanId, selectedOverlaidFlightLegIdPO, userId));
      dispatch(setFlightPathBeamsChecked(true));
      dispatch(setFlightPathWaypointsChecked(false));
      const arcLightMapVersion = getArcLightMapVersion(currentMission.antennaTypeId, currentMission.sscfFileVersion);
      const viHnaMapVersion = getViHnaMapVersion(currentMission.viHnaPolicyVersion);
      if (!initialGeojson) {
        dispatch(
          requestBeams(arcLightMapVersion, viHnaMapVersion),
        );
        setInitialGeojson(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [missionPlanId, loadingFlightLegsSuccess, selectedOverlaidFlightLegIdPO]);

  // Change zoom and location of the map to fit flight leg
  useEffect(() => {
    if (
      selectedOverlaidFlightLegIdPO &&
      flightLegServiceAlerts &&
      flightLegServiceAlerts[selectedOverlaidFlightLegIdPO] &&
      flightLegServiceAlerts[selectedOverlaidFlightLegIdPO].waypoints
    ) {
      const flightPathBbox = bbox(
        lineString(
          flightLegServiceAlerts[selectedOverlaidFlightLegIdPO].waypoints.map((waypoint: Waypoint) => [
            waypoint.longitude,
            waypoint.latitude,
          ]),
        ),
      );
      if (map && map.fitBounds) {
        map.fitBounds(
          {
            east: flightPathBbox[2],
            north: flightPathBbox[3],
            south: flightPathBbox[1],
            west: flightPathBbox[0],
          } as google.maps.LatLngBoundsLiteral,
          FTV_MAP_ZOOM_BUFFER,
        );
      }
    }
  }, [dispatch, flightLegServiceAlerts, map, selectedOverlaidFlightLegIdPO]);

  return !isNil(selectedOverlaidFlightLegIdPO) && !isNil(flightLegServiceAlerts) ? (
    <React.Fragment>
      <MapContext.Consumer>
        {map => (
          <StyledEngineProvider injectFirst>
            <ThemeProvider theme={appTheme}>
              <React.Fragment>
                <MptFlightPathWaypointsContainer
                  map={map}
                  flightLeg={flightLegs[selectedOverlaidFlightLegIdPO]}
                  flightLegServiceAlerts={flightLegServiceAlerts}
                />
                <MptFlightPathContainer
                  map={map}
                  flightLeg={flightLegs[selectedOverlaidFlightLegIdPO]}
                  flightLegServiceAlerts={flightLegServiceAlerts}
                  addBeamToMap={addBeamToMapHandler}
                />

                <MptServiceAlertsContainer
                  map={map}
                  flightLeg={flightLegs[selectedOverlaidFlightLegIdPO]}
                  flightLegServiceAlerts={flightLegServiceAlerts}
                  isRequestingFlightLegServiceAlerts={isRequestingFlightLegServiceAlerts}
                  overlaidFlightLegHidden={overlaidFlightLegHidden}
                  setHoveredBeam={setHoveredBeamHandler}
                  mapId={PERFORMANCE_OUTLOOK_MAP_ID}
                />
              </React.Fragment>
            </ThemeProvider>
          </StyledEngineProvider>
        )}
      </MapContext.Consumer>
    </React.Fragment>
  ) : (
    <></>
  );
}

export default PerformanceOutlookMap;
